/**
 * CleanupStaleResourcesDialog (Story 32.8 — ADR-0004 §Catalog Refresh
 * Strategy ">90 days unavailable" user-initiated cleanup tool)
 *
 * Centered modal Dialog (directive §4 — NEVER a side Sheet) with TWO views
 * inside ONE dialog (no stacked modals):
 *  - List view: one row per stale resource (platform badge, day count,
 *    dependent summary) with a per-row "Delete…" affordance.
 *  - Confirm view (destructive, §4.4): replaces the list, spells out the
 *    explicit cascade ("also removes N client integrations") + the
 *    recovery-by-rediscovery note.
 *
 * NEVER auto-delete (ADR-0004 §Anti-Patterns 7): there is no bulk control,
 * no select-all — exactly ONE resource per confirmed DELETE request.
 * Reviewers: adding any bulk affordance here is a review failure.
 */

import { useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import { Broom, Warning } from "@phosphor-icons/react";
import { apiDelete } from "../../lib/apiClient";
import { platformBadge } from "../../lib/integrations";
import type { ToastType } from "../../hooks/useToast";

// ============================================================================
// Types (mirror GET /api/integrations/stale-resources)
// ============================================================================

export interface StaleResourceDependent {
  integrationId: string;
  clientId: string;
  clientName: string;
  platform: string;
}

export interface StaleGrantResource {
  id: string;
  platform: string;
  externalAccountId: string;
  externalAccountName: string | null;
  /** Epoch-ms. */
  unavailableSinceAt: number;
  dependents: StaleResourceDependent[];
}

interface CleanupStaleResourcesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resources: StaleGrantResource[];
  showToast: (type: ToastType, message: string) => void;
}

// ============================================================================
// Helpers
// ============================================================================

const DAY_MS = 86_400_000;

function daysUnavailable(unavailableSinceAt: number): number {
  return Math.max(0, Math.floor((Date.now() - unavailableSinceAt) / DAY_MS));
}

function resourceDisplayName(resource: StaleGrantResource): string {
  return resource.externalAccountName ?? resource.externalAccountId;
}

function platformDisplayName(platform: string): string {
  return platformBadge(platform)?.displayName ?? platform;
}

function dependentSummary(resource: StaleGrantResource): string {
  const deps = resource.dependents;
  if (deps.length === 0) return "Not attached to any client";
  if (deps.length === 1) {
    const dep = deps[0]!;
    return `Used by ${dep.clientName} (${platformDisplayName(dep.platform)})`;
  }
  return `Used by ${deps.length} client integrations`;
}

const VIEW_TRANSITION = { duration: 0.15, ease: "easeOut" as const };

// ============================================================================
// Component
// ============================================================================

export function CleanupStaleResourcesDialog({
  open,
  onOpenChange,
  resources,
  showToast,
}: CleanupStaleResourcesDialogProps) {
  const queryClient = useQueryClient();
  // Confirm view target — null means the list view is showing.
  const [confirmTarget, setConfirmTarget] = useState<StaleGrantResource | null>(null);
  const [deleting, setDeleting] = useState(false);

  // §4.2 guard: close blocked while the delete mutation is pending.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && deleting) return;
      if (!next) setConfirmTarget(null);
      onOpenChange(next);
    },
    [deleting, onOpenChange]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    const name = resourceDisplayName(target);
    setDeleting(true);
    try {
      const res = await apiDelete(`/api/integrations/stale-resources/${target.id}`);

      if (!res.ok) {
        const errorJson = (await res.json().catch(() => null)) as { detail?: string } | null;
        if (res.status === 409) {
          // Recovered on the last catalog refresh — the row vanishing from
          // the refetched list is the correct outcome.
          showToast(
            "error",
            errorJson?.detail ??
              "This resource is no longer stale — it may have recovered. Refresh the list."
          );
          await queryClient.invalidateQueries({ queryKey: ["stale-grant-resources"] });
          setConfirmTarget(null);
          return;
        }
        showToast("error", errorJson?.detail ?? "Failed to delete stale resource");
        return;
      }

      const json = (await res.json()) as {
        data: { removedIntegrationIds: string[] };
      };
      const removedCount = json.data.removedIntegrationIds.length;

      showToast(
        "success",
        removedCount > 0
          ? `Removed ${name} and ${removedCount} integration(s)`
          : `Removed ${name} catalog entry`
      );

      // Mirror the disconnect invalidation set. ["client-integrations"] et al
      // are PREFIX invalidations (no clientId) — the deleted dependents may
      // span several clients, targeted invalidation would miss some.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["stale-grant-resources"] }),
        queryClient.invalidateQueries({ queryKey: ["client-integrations"] }),
        queryClient.invalidateQueries({ queryKey: ["client-integrations-health"] }),
        queryClient.invalidateQueries({ queryKey: ["client-connections"] }),
        queryClient.invalidateQueries({ queryKey: ["integration-alerts"] }),
        queryClient.invalidateQueries({ queryKey: ["tenant-google-connections"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-list"] }),
      ]);

      // Back to the list view (the deleted row is gone after the refetch;
      // the empty state is reachable when this was the last row).
      setConfirmTarget(null);
    } catch {
      showToast("error", "Failed to delete stale resource. Please try again.");
    } finally {
      setDeleting(false);
    }
  }, [confirmTarget, queryClient, showToast]);

  const confirmName = confirmTarget ? resourceDisplayName(confirmTarget) : "";
  const confirmDeps = confirmTarget?.dependents ?? [];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100vw-2rem)] sm:max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none max-h-[85vh] overflow-y-auto"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {confirmTarget === null ? (
                    // ── List view ──────────────────────────────────────────
                    <motion.div
                      key="list"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={VIEW_TRANSITION}
                    >
                      <div className="flex flex-col space-y-1.5 text-left">
                        <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                          <Broom size={18} className="text-foreground" />
                          Clean up stale resources
                        </DialogPrimitive.Title>
                        <DialogPrimitive.Description className="text-sm text-muted-foreground">
                          Resources unavailable for more than 90 days. Deleting one also
                          removes any client integrations attached to it. Nothing is ever
                          deleted automatically.
                        </DialogPrimitive.Description>
                      </div>

                      <div className="mt-5 flex flex-col">
                        {resources.length === 0 ? (
                          <p className="text-[13px] text-muted-foreground py-4">
                            No stale resources — nothing unavailable for more than 90 days.
                          </p>
                        ) : (
                          resources.map((resource, index) => {
                            const badge = platformBadge(resource.platform);
                            const depCount = resource.dependents.length;
                            return (
                              <motion.div
                                key={resource.id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.03 * (index + 1) }}
                                className="flex items-center gap-3 py-3 border-b-[0.5px] border-input last:border-b-0"
                              >
                                {badge && (
                                  <img
                                    src={badge.logoSrc}
                                    alt={badge.displayName}
                                    className="w-5 h-5 shrink-0 rounded-sm"
                                  />
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="text-[13px] font-medium text-foreground truncate">
                                    {resourceDisplayName(resource)}
                                    <span className="text-muted-foreground font-normal">
                                      {" "}
                                      · {badge?.displayName ?? resource.platform}
                                    </span>
                                  </p>
                                  <p className="text-[11px] text-muted-foreground">
                                    Unavailable for {daysUnavailable(resource.unavailableSinceAt)} days
                                  </p>
                                  <p
                                    className={`text-[12px] ${
                                      depCount > 0 ? "text-destructive" : "text-muted-foreground"
                                    }`}
                                  >
                                    {dependentSummary(resource)}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="shrink-0 rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all text-destructive hover:text-destructive"
                                  style={{ border: "0.5px solid hsl(var(--input))" }}
                                  onClick={() => setConfirmTarget(resource)}
                                >
                                  Delete…
                                </Button>
                              </motion.div>
                            );
                          })
                        )}
                      </div>
                    </motion.div>
                  ) : (
                    // ── Confirm view (per-row, destructive — §4.4) ─────────
                    <motion.div
                      key="confirm"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={VIEW_TRANSITION}
                    >
                      <div className="flex flex-col space-y-1.5 text-left">
                        <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                          <Warning size={18} className="text-destructive" />
                          Delete stale resource?
                        </DialogPrimitive.Title>
                        <DialogPrimitive.Description asChild>
                          <div className="text-sm text-muted-foreground space-y-2 pt-1">
                            {confirmDeps.length > 0 ? (
                              <p>
                                <strong className="text-foreground">{confirmName}</strong>{" "}
                                ({platformDisplayName(confirmTarget.platform)}) has been
                                unavailable for{" "}
                                {daysUnavailable(confirmTarget.unavailableSinceAt)} days.
                                Deleting it will also remove{" "}
                                <strong className="text-foreground">
                                  {confirmDeps.length} client integration(s)
                                </strong>
                                :{" "}
                                {confirmDeps
                                  .map(
                                    (dep) =>
                                      `${dep.clientName} — ${platformDisplayName(dep.platform)}`
                                  )
                                  .join(", ")}
                                . Widgets using these integrations will stop receiving data.
                              </p>
                            ) : (
                              <p>
                                <strong className="text-foreground">{confirmName}</strong>{" "}
                                ({platformDisplayName(confirmTarget.platform)}) has been
                                unavailable for{" "}
                                {daysUnavailable(confirmTarget.unavailableSinceAt)} days. It
                                is not attached to any client. This only removes the catalog
                                entry.
                              </p>
                            )}
                            <p>
                              If you regain access on the platform side, the daily catalog
                              refresh will re-discover it.
                            </p>
                          </div>
                        </DialogPrimitive.Description>
                      </div>

                      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                        <Button
                          variant="ghost"
                          className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                          style={{ border: "0.5px solid hsl(var(--input))" }}
                          onClick={() => setConfirmTarget(null)}
                          disabled={deleting}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          className="rounded-xl cursor-pointer"
                          onClick={handleConfirmDelete}
                          disabled={deleting}
                        >
                          {deleting ? "Deleting..." : "Delete resource"}
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
